import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { fetchTranslationsThunk } from '@/app/store/thunks/translationThunks';
import type { AppDispatch, RootState } from '@/app/store';

/**
 * Generic hook for resolving entity-level translations.
 *
 * Resolution priority:
 *   1. JSON locale file (caller checks before calling getTranslation)
 *   2. DB translation cache (fetched once per locale from SysTranslation table)
 *   3. Raw fallback value (typically the English DB field)
 *
 * For en-US the hook always returns the fallback because English text lives
 * in the source entity table, not in SysTranslation.
 */
export function useEntityTranslation() {
    const { currentLanguage } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const translationCache = useSelector((state: RootState) => state.translation.cache);
    const loadedLocales = useSelector((state: RootState) => state.translation.loadedLocales);

    // Fetch translations for current locale on mount / language change
    useEffect(() => {
        if (
            currentLanguage &&
            currentLanguage !== 'en-US' &&
            !loadedLocales.includes(currentLanguage)
        ) {
            dispatch(fetchTranslationsThunk(currentLanguage) as any);
        }
    }, [currentLanguage, loadedLocales, dispatch]);

    /**
     * Resolve a translated value for an entity field.
     *
     * @param entityType - e.g. "sys_app", "sys_role"
     * @param entityId   - the numeric PK of the source entity
     * @param fieldName  - e.g. "description", "app_name"
     * @param fallback   - raw DB value / JSON locale value to use if no DB translation exists
     */
    const getTranslation = useCallback(
        (
            entityType: string,
            entityId: number,
            fieldName: string,
            fallback: string
        ): string => {
            // For en-US, always use fallback (raw DB value / JSON locale)
            if (!currentLanguage || currentLanguage === 'en-US') return fallback;

            // Check DB translation cache
            const translations = translationCache[currentLanguage];
            if (translations) {
                const match = translations.find(
                    (t) =>
                        t.entityType === entityType &&
                        t.entityId === entityId &&
                        t.fieldName === fieldName
                );
                if (match) return match.value;
            }

            // Fallback to raw value
            return fallback;
        },
        [currentLanguage, translationCache]
    );

    return { getTranslation };
}
